import React, { FC, useEffect, useState, useContext } from 'react';
import { Button, Popover, DropdownBox, List } from 'tea-component';
import { useIntl } from 'umi';
import cls from 'classnames';
import { DownLoadConfig } from './instance/config';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import DownloadSettingModal from './components/DownloadSettingModal';
import DownloadFormModal from './components/DownloadFormModal';
import EChartDownloadModal from './components/NewEChartDownloadModal';
import DownLoadBubble from '@/pages/intelligence/components/DownLoad/DownLoadBubble';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import type { ModalProps } from 'tea-component';
import { BusinessType } from './types';
import './index.scss';
import { isSafari } from './utils';
import type { DownloadSettingModalProps } from './types';
import { getDownloadTaskId } from '@/pages/intelligence/services/download';
import { getBasePath, storage } from '@/utils/utils';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import type { PCConsolePageId } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import { btnPointInter } from '@/pages/intelligence/utils/topChartsBuriedPointCfg';
import { ChartContext } from '@/pages/intelligence/layouts';
import useChartExportTableHooks from '@/pages/intelligence/pages/TopCharts/_hooks/useChartExportTableHooks';
import { downloadAction } from '@intelligence/components/DownloadCommon/DownloadPage';

interface ExportTableProps {
  total?: number;
  maxSize?: number;
  // pageId?: string;
  entityType: string;
  businessType?: BusinessType | any;
  isDisableDownload?: boolean;
  isDisableDataAmount?: boolean;
  immediateDownload?: boolean; // 点击下载是否只下载excel
  isUseFormDownload?: boolean;
  classModalName?: string;
  segpaVa?: string;
  excelName?: string;
  dataType?: string;
  downloadTitleTips?: string;
  isShowMaxDownloadRowsTips?: boolean;
  downloadModalSize?: ModalProps['size'];
  downloadLogConfigKey?: string;
  tableParams?: { [key: string]: any };
  hasBubbleCloseIcon?: boolean;
  isShowExportAllDataTip?: boolean;
  isOnlyExportAllData?: DownloadSettingModalProps['isOnlyExportAllData'];
  isNeedCheckedLines?: boolean;
  // 下载数据量的时候只保留导出所有和导出所选数据
  isAllAndChecked?: boolean;
  defaultCheckedLines?: boolean;
  isGameTopCharts?: boolean;
  isStudio?: boolean;
  isCalibrated?: boolean;
  regionOptions?: any;
  onBubbleClose?: Function;
  isSyncPageSizeWithSelectTop?: DownloadSettingModalProps['isSyncPageSizeWithSelectTop'];
  useDownloadAmountType?: DownloadSettingModalProps['useDownloadAmountType'];
  texts?: DownloadSettingModalProps['texts'];
  getCheckedLinesCfg?: DownloadSettingModalProps['getCheckedLinesCfg'];
  pageId?: PCConsolePageId;
  newStyleFileName?: string;
  gsdDevicesFlatten?: DownloadSettingModalProps['gsdDevicesFlatten'];
  gsdSegments?: DownloadSettingModalProps['gsdSegments'];
  downloadLogConfig?: btnPointInter;
  downloadBtnLogConfig?: btnPointInter;
  showDropdownBtnList?: boolean; // 展示hover下拉按钮选项
  chartDownloadConfig?: any; // 图标下载弹窗的预览图配置
  immediateDownloadCharts?: boolean; // 点击下载是否只下载图表
  isCoutriesPage?: boolean; // 是否是详情countries页面
  metricSelectorOptions?: any[];
  tabType?: string;
  buttonType?:
  | 'link'
  | 'text'
  | 'primary'
  | 'weak'
  | 'pay'
  | 'error'
  | 'icon'
  | undefined;
  buttonClassName?: string;
  // 是否启用web下载
  useWebDownload?: boolean;
  downloadKey?: string;
  /**
   * 前端下载，跳转下载页面时的query参数
   */
  downloadQuery?: IRecord;
  stickText?: string;
  hideStickText?: boolean;
  dataSource?: string; // 指定来源数据
  getSortedGameIds?: (gameIds?: string[] | null) => Promise<string[]>;
  downloadPageFun?: (any?: string[] | null) => Promise<string[]>;
}

const ExportTable: FC<ExportTableProps> = (props) => {
  const {
    total,
    maxSize,
    entityType,
    businessType,
    downloadModalSize = 'm',
    downloadLogConfigKey,
    isDisableDownload = false,
    isDisableDataAmount = false,
    tableParams = {},
    classModalName,
    downloadTitleTips,
    isShowMaxDownloadRowsTips = true,
    hasBubbleCloseIcon = false,
    onBubbleClose,
    excelName,
    dataType,
    segpaVa,
    isShowExportAllDataTip = false, // 是否展示ExportAll的提示
    isOnlyExportAllData = false,
    isAllAndChecked = false,
    immediateDownload = false, // 是否直接下载，true 直接在当前页面下载，false 跳转到下载页面进行下载
    isUseFormDownload = false, // 是否使用新的
    isNeedCheckedLines = false, // 是否展示宽表勾选联动下载
    defaultCheckedLines = false, // 是否自动选择宽表勾选联动下载
    isGameTopCharts = false, // 是否有宽表勾选联动下载
    isSyncPageSizeWithSelectTop = false,
    useDownloadAmountType,
    texts = {},
    getCheckedLinesCfg,
    regionOptions,
    // 主要用于pc端MetricsTable榜单页、console端MetricsTable榜单页、pcConsole联合id MetricsTable榜单页下载时，获取数据源指标禁用状态的方法内部 走不同的判断逻辑
    pageId = TopchartPageId.MetricsTable,
    newStyleFileName,
    gsdDevicesFlatten,
    gsdSegments,
    isCalibrated = false,
    downloadLogConfig,
    downloadBtnLogConfig,
    showDropdownBtnList,
    chartDownloadConfig,
    immediateDownloadCharts,
    isStudio = false,
    metricSelectorOptions = [],
    tabType = '',
    buttonType,
    buttonClassName,
    isCoutriesPage = false,
    useWebDownload = false,
    downloadKey = '',
    downloadQuery,
    stickText = '',
    hideStickText = false,
    dataSource,
    getSortedGameIds,
    downloadPageFun,
  } = props;
  const { formatMessage } = useIntl();

  // console.debug('Export table', { metricSelectorOptions });

  // 根据ChartContext消费者，获取statsChartOption值
  const { statsChartOption } = useContext(ChartContext);
  // console.debug('ExportTableExportTable', props);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [chartDownloadModalVisible, setChartDownloadModalVisible] =
    useState(false);
  const [bubbleShow, setBubbleShow] = useState(hasBubbleCloseIcon);

  useEffect(() => {
    setBubbleShow(hasBubbleCloseIcon);
  }, [hasBubbleCloseIcon]);

  // 获取可视化下载配置hooks
  const { rankNum, requestLoading, getTopchartsRank, cardConfig } =
    useChartExportTableHooks({
      entityType,
      tableParams,
      chartDownloadConfig,
    });

  const openCommonDownloadPage = async (params: any) => {
    // 详情页 demographics sensortower 直接文件流下载
    if (businessType === 'mobile-detail-demographics' && params?.datasource === 'sensortower') {
      try {
        setLoading(true);
        storage.setItem('dbi_mobile_details_demographics_download_params', { businessType, params });
        if (isSafari()) {
          const page: any = window.open('', '_blank');
          page.location = `${location.origin}${getBasePath()}/intelligence/download?entityType=${entityType}&isShowMaxDownloadRowsTips=${isShowMaxDownloadRowsTips}`;
        } else {
          window.open(
            `${getBasePath()}/intelligence/download?entityType=${entityType}&isShowMaxDownloadRowsTips=${isShowMaxDownloadRowsTips}`,
            '_blank',
          );
        }
      } catch (e) {
        console.warn(e);
      } finally {
        setLoading(false);
      }
      return;
    }
    // 非 sensortower 下载时，及时清理旧的 localStorage
    if (businessType === 'mobile-detail-demographics') {
      storage.removeItem('dbi_mobile_details_demographics_download_params');
    }
    try {
      setLoading(true);
      const api = DownLoadConfig?.[businessType]?.apiUrl || '';
      if (!api) return;
      if (isSafari()) {
        const page: any = window.open('', '_blank');
        const taskId = await getDownloadTaskId(api, params);
        page.location = `${location.origin}/download?entityType=${entityType}&taskId=${taskId}&isShowMaxDownloadRowsTips${isShowMaxDownloadRowsTips}`;
      } else {
        const taskId = await getDownloadTaskId(api, params);
        window.open(
          `${getBasePath()}/intelligence/download?entityType=${entityType}&taskId=${taskId}&isShowMaxDownloadRowsTips=${isShowMaxDownloadRowsTips}`,
          '_blank',
        );
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
      setModalVisible(false);
    }
  };

  const handleDownloadLog = (logConfigKey?: string) => {
    // @ts-ignore
    const logConfig = dbiBtnLogMes[logConfigKey];
    if (!logConfig) return;
    buriedBtnPoint(logConfig);
  };
  const handleDownloadClickBtnLog = () => {
    downloadBtnLogConfig && buriedBtnPoint(downloadBtnLogConfig);
  };
  const handleDownloadClick = () => {
    if (immediateDownloadCharts) {
      getTopchartsRank();
      return setChartDownloadModalVisible(true);
    }
    if (immediateDownload) {
      // 直接下载，不需要弹窗
      if (useWebDownload) {
        downloadAction({
          downLoadParams: tableParams,
          downloadKey,
        });
        handleDownloadLog(downloadLogConfigKey);
      } else {
        handleDownloadLog(downloadLogConfigKey);
        openCommonDownloadPage(tableParams);
      }
    } else if (isUseFormDownload) {
      setBubbleShow(false);
      setFormModalVisible(true);
    } else {
      setBubbleShow(false);
      setModalVisible(true);
    }

    downloadBtnLogConfig && handleDownloadClickBtnLog();
  };

  const handleDownloadCharts = () => {
    setChartDownloadModalVisible(true);
    // 打开可视化下载前，先调榜单rank排名接口
    if (showDropdownBtnList) {
      getTopchartsRank();
    }
  };

  return (
    <>
      <DownLoadBubble
        style={{ width: 400 }}
        hasCloseIcon={hasBubbleCloseIcon}
        visible={
          !modalVisible && bubbleShow ? bubbleShow : undefined /* 不要给false*/
        }
        content={
          DownLoadConfig?.[businessType]?.bubbleText?.indexOf('dbi') > -1
            ? formatMessage({ id: DownLoadConfig?.[businessType]?.bubbleText })
            : DownLoadConfig?.[businessType]?.bubbleText
        }
        onClose={() => {
          if (onBubbleClose instanceof Function) {
            onBubbleClose();
            setBubbleShow(false);
          }
        }}
      >
        {showDropdownBtnList ? (
          <Popover
            placement="bottom-end"
            trigger="hover"
            overlay={
              <DropdownBox>
                <List type="option">
                  <List.Item onClick={handleDownloadClick}>
                    {formatMessage({
                      id: 'dbi_download_modal_download_the_metrics_data',
                    })}
                  </List.Item>
                  <List.Item onClick={handleDownloadCharts}>
                    {formatMessage({
                      id: 'dbi_download_modal_download_the_charts',
                    })}
                  </List.Item>
                </List>
              </DropdownBox>
            }
          >
            <Button
              icon="download"
              // title={formatMessage({ id: 'dbi_download_data' })}
              onMouseEnter={() => setBubbleShow(true)}
              onMouseLeave={() => setBubbleShow(false)}
              loading={loading}
              className="ns-btn--icon trend-justify-btn"
              disabled={isDisableDownload}
            />
          </Popover>
        ) : (
          <Button
            icon="download"
            type={buttonType}
            // title={formatMessage({ id: 'dbi_download_data' })}
            onMouseEnter={() => setBubbleShow(true)}
            onMouseLeave={() => setBubbleShow(false)}
            loading={loading}
            className={cls(
              buttonClassName,
              buttonType ? '' : 'ns-btn--icon trend-justify-btn',
            )}
            // className="ns-btn--icon trend-justify-btn"
            onClick={handleDownloadClick}
            disabled={isDisableDownload}
          />
        )}
        {modalVisible && (
          <DownloadSettingModal
            total={Number(total || 0)}
            maxSize={maxSize}
            entityType={entityType}
            visible={modalVisible}
            loading={loading}
            businessType={isStudio ? `${businessType}-studio` : businessType}
            downloadModalSize={downloadModalSize}
            downloadLogConfigKey={downloadLogConfigKey}
            setLoading={setLoading}
            tableParams={tableParams}
            classModalName={classModalName}
            downloadTitleTips={downloadTitleTips}
            isDisableDataAmount={isDisableDataAmount}
            isShowMaxDownloadRowsTips={isShowMaxDownloadRowsTips}
            setModalVisible={setModalVisible}
            segpaVa={segpaVa}
            isShowExportAllDataTip={isShowExportAllDataTip}
            isOnlyExportAllData={isOnlyExportAllData}
            isAllAndChecked={isAllAndChecked}
            isNeedCheckedLines={isNeedCheckedLines}
            defaultCheckedLines={defaultCheckedLines}
            isGameTopCharts={isGameTopCharts}
            isSyncPageSizeWithSelectTop={isSyncPageSizeWithSelectTop}
            useDownloadAmountType={useDownloadAmountType}
            texts={texts}
            getCheckedLinesCfg={getCheckedLinesCfg}
            dataType={dataType}
            regionOptions={regionOptions}
            excelName={excelName}
            pageId={pageId}
            newStyleFileName={newStyleFileName}
            gsdDevicesFlatten={gsdDevicesFlatten}
            gsdSegments={gsdSegments}
            isCalibrated={isCalibrated}
            downloadLogConfig={downloadLogConfig}
            isStudio={isStudio}
            metricSelectorOptions={metricSelectorOptions}
            tabType={tabType}
            isCoutriesPage={isCoutriesPage}
            useWebDownload={useWebDownload}
            downloadKey={downloadKey}
            downloadQuery={downloadQuery}
            hideStickText={hideStickText}
            stickText={stickText}
            dataSource={dataSource}
            getSortedGameIds={getSortedGameIds}
            downloadPageFun={downloadPageFun}
          />
        )}
        {/* 新配置化组件下载弹窗 */}
        {formModalVisible && (
          <DownloadFormModal
            total={Number(total || 0)}
            maxSize={maxSize}
            entityType={entityType}
            visible={formModalVisible}
            loading={loading}
            businessType={isStudio ? `${businessType}-studio` : businessType}
            downloadModalSize={downloadModalSize}
            downloadLogConfigKey={downloadLogConfigKey}
            setLoading={setLoading}
            tableParams={tableParams}
            classModalName={classModalName}
            downloadTitleTips={downloadTitleTips}
            isDisableDataAmount={isDisableDataAmount}
            isShowMaxDownloadRowsTips={isShowMaxDownloadRowsTips}
            setModalVisible={setFormModalVisible}
            segpaVa={segpaVa}
            isShowExportAllDataTip={isShowExportAllDataTip}
            isOnlyExportAllData={isOnlyExportAllData}
            isNeedCheckedLines={isNeedCheckedLines}
            defaultCheckedLines={defaultCheckedLines}
            isGameTopCharts={isGameTopCharts}
            isSyncPageSizeWithSelectTop={isSyncPageSizeWithSelectTop}
            texts={texts}
            getCheckedLinesCfg={getCheckedLinesCfg}
            dataType={dataType}
            regionOptions={regionOptions}
            excelName={excelName}
            pageId={pageId}
            newStyleFileName={newStyleFileName}
            gsdDevicesFlatten={gsdDevicesFlatten}
            gsdSegments={gsdSegments}
            isCalibrated={isCalibrated}
            downloadLogConfig={downloadLogConfig}
            isStudio={isStudio}
            metricSelectorOptions={metricSelectorOptions}
            tabType={tabType}
            isCoutriesPage={isCoutriesPage}
            useWebDownload={useWebDownload}
            downloadKey={downloadKey}
            hideStickText={hideStickText}
            stickText={stickText}
          />
        )}
        {/* 原有可视化下载弹窗 */}
        {chartDownloadModalVisible && (
          <EChartDownloadModal
            downloadLogConfigKey="mobileGameDetailsStoreRankDownloadEChart"
            lineChartTitle={'lineChartTitle'}
            echartDownloadOptions={statsChartOption}
            cardConfig={cardConfig}
            rank={rankNum}
            requestLoading={requestLoading}
            visible={chartDownloadModalVisible}
            onClose={() => {
              setChartDownloadModalVisible(false);
            }}
            dataSource={dataSource}
          />
        )}
      </DownLoadBubble>
    </>
  );
};

export default ExportTable;
